// react
import { useContext, useEffect, useState } from "react";
// libs
import { AnimatePresence, motion } from "framer-motion";
import { filter, sortBy } from "lodash";
import { useMount, useToggle } from "react-use";
import { useQueryParam } from "../lib/hooks/react-router";
// components
import { Box } from "../components/Box";
import { Container } from "../components/Container";
import { FlexContainer, FlexItem } from "../components/Flex";
import { LayoutScroll } from "../layout/LayoutScroll";
import { SearchFilters } from "../components/search/SearchFilters";
import SearchHeader from "../components/SearchHeader";
import { SearchItem } from "../components/SearchItem";
import { Text } from "../components/Text";
// context
import { ArtifactThemesContext } from "../context/ArtifactsContext";
import { LogoStyleContext } from "../context/LogoStyleContext";
// hooks
import { useGetArtifacts } from "../hooks/useGetArtifacts";
// other
import { allArtifacts } from "../helpers/transformers/artifact-theme";
import {
  handleCheckGroupChange,
  mapToInitialCheckGroupState,
} from "../helpers/transformers/utils";
import { dateRanges, themeCategories } from "../models/api";
import { Artifact } from "../models/client";

const MotionFlexItem = motion(FlexItem);

const dateInitialState = mapToInitialCheckGroupState(dateRanges);

const themeInitialState = mapToInitialCheckGroupState(themeCategories);

function Search() {
  const { setLogoStyle } = useContext(LogoStyleContext);
  const { artifactThemes } = useContext(ArtifactThemesContext);

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [matchedArtifacts, setMatchedArtifacts] = useState<Artifact[]>([]);

  const [searchParams] = useQueryParam("q");
  const [dateParams] = useQueryParam("d");
  const [themeParams] = useQueryParam("t");

  const [searchInput, setSearchInput] = useState("");

  const [dateFilters, setDateFilters] = useState(dateInitialState);
  const [themeFilters, setThemeFilters] = useState(themeInitialState);

  const [showFilters, setShowFilters] = useToggle(false);

  const [isLoaded] = useGetArtifacts();

  const handleSearchInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchInput(e.target.value);
  };

  const handleDatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyCheckPair = {
      key: e.target.value,
      checked: e.target.checked,
    };
    setDateFilters(handleCheckGroupChange(dateFilters, keyCheckPair));
  };

  const handleThemesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyCheckPair = {
      key: e.target.value,
      checked: e.target.checked,
    };
    setThemeFilters(handleCheckGroupChange(themeFilters, keyCheckPair));
  };

  useEffect(() => {
    setLogoStyle("dark");
  }, [setLogoStyle]);

  useEffect(() => {
    const artifacts = allArtifacts(artifactThemes);
    const sortedArtifacts = sortBy(artifacts, "name");
    setArtifacts(sortedArtifacts);
  }, [artifactThemes]);

  useMount(() => {
    setSearchInput(searchParams || "");
    if (dateParams) {
      const dateParamArr = dateParams.split(",");
      const dates = dateFilters.map((dateState) => {
        return {
          ...dateState,
          checked: dateParamArr.includes(dateState.key),
        };
      });
      setDateFilters(dates);
    }
    if (themeParams) {
      const themeParamArr = themeParams.split(",");
      const themes = themeFilters.map((themeState) => {
        return {
          ...themeState,
          checked: themeParamArr.includes(themeState.key),
        };
      });
      setThemeFilters(themes);
    }
  });

  useEffect(() => {
    const regex = new RegExp(searchInput, "gi");
    const matched = filter(
      artifacts,
      (artifact) => (artifact.name.match(regex) || []).length > 0
    );

    setMatchedArtifacts(matched);
  }, [searchInput, artifacts]);

  if (!isLoaded) {
    return <div>loading...</div>;
  }

  return (
    <LayoutScroll>
      <Box backgroundColor="violet" color="white">
        <Box pt={["104px", null, "160px"]} pb={[4, null, 5]}>
          <Container maxWidth={3}>
            <Box mb={4}>
              <Text variant="h4" as="div">
                You searched for: "{searchParams}"
              </Text>
            </Box>
            <SearchHeader
              searchInputValue={searchInput}
              onSearchInputChange={handleSearchInputChange}
              onToggleFilterClick={() => setShowFilters()}
            />
            {/* <Divider /> */}

            <AnimatePresence>
              {showFilters && (
                <SearchFilters
                  dateState={dateFilters}
                  themeState={themeFilters}
                  onDatesChange={handleDatesChange}
                  onThemesChange={handleThemesChange}
                />
              )}
            </AnimatePresence>
          </Container>
        </Box>
      </Box>
      <Box backgroundColor="white" py={4} minHeight="35vh">
        <Container maxWidth={3}>
          <Box my={4}>
            <Text variant="h4" as="div">
              {matchedArtifacts.length} results
            </Text>
          </Box>

          <Box pb={4}>
            <FlexContainer m={-3} flexWrap="wrap">
              <AnimatePresence>
                {matchedArtifacts.map((artifact, i) => (
                  <MotionFlexItem
                    key={i}
                    flex={["0 0 50%", null, "0 0 25%"]}
                    p={3}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <SearchItem
                      link={`/themes/${artifact.themeId}?a=${artifact.id}`}
                      image={artifact.previewImg}
                      title={artifact.name}
                      tags={["1970s"]}
                    />
                  </MotionFlexItem>
                ))}
              </AnimatePresence>
            </FlexContainer>
          </Box>
        </Container>
      </Box>
    </LayoutScroll>
  );
}

export default Search;
